import { getLogger } from '@/lib/logger/logger';

import { IframeStateMachine } from './IframeStateMachine';

const logger = getLogger('SdkIframeService');

/**
 * SDK iframe 서비스의 공통 추상 클래스.
 * HWP/HCell 서비스가 공유하는 ready 상태 관리, i18n 에러 생성 등을 제공.
 */
export abstract class SdkIframeService {
  protected readonly stateMachine: IframeStateMachine;
  protected readyPromise: Promise<void> | null = null;
  protected readyResolve: (() => void) | null = null;
  protected readyReject: ((error: Error) => void) | null = null;

  abstract readonly sdkName: string;
  abstract readonly defaultTimeoutMs: number;
  /** i18n 네임스페이스 (예: 'hwp', 'hcell'). 기본값은 sdkName.toLowerCase() */
  get i18nNamespace(): string {
    return this.sdkName.toLowerCase();
  }
  abstract cleanup(): void;

  constructor() {
    // sdkName은 아직 초기화 전이므로 일반 이름 사용
    this.stateMachine = new IframeStateMachine('SdkIframe');
  }

  /** 현재 상태 머신이 ready인지 확인 */
  get isReady(): boolean {
    return this.stateMachine.is('ready');
  }

  /** SDK 준비 완료 처리 */
  setReady(): void {
    if (this.isReady) {
      logger.info(`${this.sdkName}: 이미 준비 완료됨 - 중복 호출 무시`);
      return;
    }

    logger.info(`${this.sdkName}: setReady() 호출됨 - SDK 준비 완료`);

    // initializing 상태가 아니면 먼저 전이
    const sm = this.stateMachine;
    if (sm.is('idle')) sm.transition('loading');
    if (sm.is('loading')) sm.transition('loaded');
    if (sm.is('loaded')) sm.transition('initializing');
    sm.transition('ready');

    if (this.readyResolve) {
      logger.info(`${this.sdkName}: readyResolve 호출 - 대기 중인 작업들 시작`);
      this.readyResolve();
    }
  }

  /** SDK 준비될 때까지 대기 */
  async waitForReady(timeoutMs?: number): Promise<void> {
    const timeout = timeoutMs ?? this.defaultTimeoutMs;

    if (this.isReady) {
      logger.info(`${this.sdkName}: 이미 준비됨`);
      return;
    }

    if (this.readyPromise) {
      logger.info(`${this.sdkName}: 기존 대기 중인 Promise 반환`);
      return this.readyPromise;
    }

    logger.info(`${this.sdkName}: ${timeout}ms 동안 초기화 대기 시작`);

    this.readyPromise = new Promise<void>((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;

      setTimeout(() => {
        if (!this.isReady) {
          const error = this.createI18nError('initializationTimeout');
          logger.error(`${this.sdkName}: 타임아웃 발생 (${timeout}ms)`);
          this.stateMachine.transition('error', error);
          this.readyReject?.(error);
        }
      }, timeout);
    });

    return this.readyPromise;
  }

  /** i18n 에러 메시지 생성 */
  createI18nError(key: string, options?: unknown): Error {
    const ns = this.i18nNamespace;
    const error = new Error();
    (error as unknown as { isI18nError?: boolean }).isI18nError = true;
    (error as unknown as { i18nKey?: string }).i18nKey = `${ns}:errors.${key}`;
    (error as unknown as { i18nOptions?: unknown }).i18nOptions = options;
    error.message = `[i18n] ${ns}:errors.${key}` + (options ? ` ${JSON.stringify(options)}` : '');
    return error;
  }
}
